import { backOffice, capabilities, howItWorks, faqs } from '../content';
import { UnitPlateHero } from './platform/UnitPlate';
import { Arrow, SmartLink, StatusChip, Swipe, usePageMeta } from '../ui';
import { HowItWorksSequence } from '../explainers';
import { CtaBand, Faq, KitStyles, PageHero, Section, SwipeLine, TwoTone, breadcrumbJsonLd, useJsonLd } from './platform/kit';
import { DigitalLink } from './platform/mockups';
import { Device, HandoverScreen, HomeScreen } from '../phone';
import { sectorByKey } from '../variants/c/sectors';
import { BackOffice } from '../insights';
import { heroUnits } from '../heroUnits';


// The capability map, grouped by the moment in a unit's life where each one matters.
const STAGES: { k: string; t: string; d: string; keys: string[] }[] = [
  { k: '01', t: 'Leaves the factory', d: 'An identity for every unit, fed from your ERP.', keys: ['identity', 'dpp'] },
  { k: '02', t: 'Changes hands', d: 'Registered at the real moment, with the dealer kept in.', keys: ['registration', 'dealers', 'ownership'] },
  { k: '03', t: 'Needs help', d: 'Every case, part and answer starts from the serial.', keys: ['service', 'parts', 'support', 'recalls'] },
];

const PLATFORM_FAQS = [
  faqs[0],
  faqs[2],
  {
    q: 'Can we start with one product line?',
    a: 'Yes, and we recommend it. Start with one product line and one dealer group. Connect runs month to month, so you can prove it before you widen it.',
  },
  faqs[1],
  faqs[3],
];

function CapabilityMap() {
  return (
    <div className="pf-map">
      {/* the spine: the one record everything hangs off */}
      <div className="pf-spine-head" data-reveal>
        <div className="pf-record">
          <span className="text-[13.5px] font-semibold text-bm-lime">The unit record</span>
          <span className="mt-1 block text-[15px] font-semibold text-white">One per serial. On your domain. Fed by your ERP.</span>
        </div>
      </div>
      <div className="pf-stages">
        {STAGES.map((s, si) => (
          <div key={s.k} className="pf-stage" data-reveal style={{ ['--reveal-delay' as string]: `${si * 0.06}s` }}>
            <div className="pf-stage-head">
              <span className="pf-node" aria-hidden="true" />
              <div className="font-mono text-[12px] text-bm-muted">{s.k}</div>
              <h3 className="mt-1 text-[22px] font-bold tracking-[-0.02em] text-bm-indigo">{s.t}</h3>
            </div>
            <ul className="pf-caps">
              {s.keys.map((key) => {
                const c = capabilities.find((x) => x.key === key)!;
                return (
                  <li key={key}>
                    <SmartLink href={c.href} className="pf-cap group">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <div className="text-[17px] font-semibold text-bm-indigo group-hover:text-bm-emerald">{c.title}</div>
                          <div className="mt-0.5 text-[15px] font-medium text-bm-ink">{c.line}</div>
                        </div>
                        <StatusChip status={c.status} />
                      </div>
                    </SmartLink>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

function Surfaces() {
  return (
    <>
      <div className="grid gap-6 md:grid-cols-2">
        {[
          {
            k: 'Owner',
            t: 'A page for their exact unit',
            d: 'They scan with the phone camera. The manual, parts, warranty and help for their build.',
            phone: <Device s={sectorByKey('heatpump')} label="The owner's page for a Halvard AW-9 heat pump"><HomeScreen s={sectorByKey('heatpump')} /></Device>,
          },
          {
            k: 'Dealer',
            t: 'Sign-off on site, on a phone',
            d: 'Staff scan the same code and sign off with the customer. Warranty starts that day.',
            phone: <Device s={sectorByKey('oven')} label="An installer's sign-off for a Fornell combi oven"><HandoverScreen s={sectorByKey('oven')} /></Device>,
          },
        ].map((s, i) => (
          <div key={s.k} className="pf-surface" data-reveal style={{ ['--reveal-delay' as string]: `${i * 0.08}s` }}>
            <div className="pf-surface-stage pf-mock">
              <div className="pf-zoom">{s.phone}</div>
            </div>
            <div className="p-6 sm:p-8">
              <div className="text-[14.5px] font-semibold text-bm-emerald">{s.k}</div>
              <h3 className="t-h3 mt-2">{s.t}</h3>
              <p className="mt-3 text-[16px] text-bm-muted">{s.d}</p>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

function Standards() {
  return (
    <div className="grid gap-px overflow-hidden rounded-[22px] border border-bm-line bg-bm-line lg:grid-cols-[1.4fr_1fr_1fr]">
      <div className="bg-white p-6 sm:p-8" data-reveal>
        <div className="text-[14.5px] font-semibold text-bm-emerald">GS1 Digital Link</div>
        <h3 className="t-h3 mt-2">A code that is a normal web address</h3>
        <p className="mt-3 text-[15.5px] text-bm-muted">
          Your domain, the product&rsquo;s GTIN and the unit&rsquo;s serial. Any phone reads it, and it can carry passport data.
        </p>
        <div className="pf-mock mt-6 hidden sm:block">
          <DigitalLink />
        </div>
      </div>
      <div className="bg-white p-6 sm:p-8" data-reveal style={{ ['--reveal-delay' as string]: '0.06s' }}>
        <div className="text-[14.5px] font-semibold text-bm-emerald">Passkeys</div>
        <h3 className="t-h3 mt-2">No passwords for owners or dealers</h3>
        <p className="mt-3 text-[15.5px] text-bm-muted">
          Sign-in by one-time code or a passkey built into the phone. Staff can be held to two-factor.
        </p>
        <div className="pf-mock mt-6 hidden rounded-2xl bg-bm-alt p-4 sm:block">
          <div className="mx-auto max-w-[220px] rounded-xl bg-white p-4 text-center shadow-[0_0_0_1px_#E4E3F0]">
            <svg width="28" height="28" viewBox="0 0 24 24" className="mx-auto" aria-hidden="true">
              <circle cx="9" cy="8" r="4" fill="none" stroke="#241E61" strokeWidth="1.8" />
              <path d="M2.5 20c.8-3.6 3.4-5.5 6.5-5.5 1.2 0 2.3.3 3.2.8" fill="none" stroke="#241E61" strokeWidth="1.8" strokeLinecap="round" />
              <circle cx="17.5" cy="13.5" r="2.6" fill="none" stroke="#2F9E58" strokeWidth="1.8" />
              <path d="M17.5 16.1V21l1.6-1.2M17.5 18.6l1.4 1" fill="none" stroke="#2F9E58" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
            <div className="mt-2 text-[13px] font-semibold text-bm-indigo">Sign in with a passkey</div>
            <div className="text-[11.5px] text-bm-muted">Use Face ID to continue</div>
          </div>
        </div>
      </div>
      <div className="bg-white p-6 sm:p-8" data-reveal style={{ ['--reveal-delay' as string]: '0.12s' }}>
        <div className="text-[14.5px] font-semibold text-bm-emerald">Open export</div>
        <h3 className="t-h3 mt-2">Your data, always yours</h3>
        <p className="mt-3 text-[15.5px] text-bm-muted">
          Export everything at any time, free. EU Data Act rights apply wherever you are.
        </p>
        <div className="mt-6 hidden space-y-2 rounded-2xl bg-bm-alt p-4 font-mono text-[12px] text-bm-indigo">
          {['Products and units', 'Owners and registrations', 'Cases, returns and documents'].map((f) => (
            <div key={f} className="flex items-center justify-between rounded-lg bg-white px-3 py-2 shadow-[0_0_0_1px_#E4E3F0]">
              <span className="font-display text-[12.5px]">{f}</span>
              <span className="text-bm-emerald">Export</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Platform() {
  usePageMeta(
    'Platform: product registration, warranty, dealers and service by serial number',
    'One record for every unit you make. Registration, warranty, dealer handover, service, parts, ownership transfer and Digital Product Passports, under your brand and connected to your ERP.'
  );
  useJsonLd('bc-platform', breadcrumbJsonLd([{ name: 'Home', path: '/' }, { name: 'Platform', path: '/platform' }]));

  return (
    <div className="pf">
      <KitStyles />
      <PageStyles />
      <PageHero
        title={<SwipeLine parts={['One record for', 'every unit', 'you make.']} draw />}
        lede="Registration, warranty, service, parts and passports, held against the serial."
        aside={<UnitPlateHero />}
      />

      <Section id="how" alt>
        <div className="grid gap-6 lg:grid-cols-[1fr_1fr] lg:items-end">
          <h2 className="t-h2" data-reveal>
            {howItWorks.title[0]} <Swipe>{howItWorks.title[1]}</Swipe>
          </h2>
          <p className="t-lede max-w-xl lg:justify-self-end" data-reveal>
            Then owners scan the same code for manuals, parts and help.
          </p>
        </div>
        <div className="mt-12" data-reveal>
          <HowItWorksSequence />
        </div>
      </Section>

      <Section id="capabilities">
        <div className="max-w-3xl" data-reveal>
          <TwoTone lead="Everything hangs off the serial." />
        </div>
        <div className="mt-14">
          <CapabilityMap />
        </div>
      </Section>

      <section className="pf-belief" aria-label="Why it matters">
        <div className="wrap">
          <div className="frame">
            <div className="relative">
              <img src="/images/v2/gen/outdoor-caravan.jpg" alt="A couple unpacking camping chairs beside a touring caravan at a lakeside site at dusk" width={1536} height={1024} loading="lazy" className="aspect-[4/5] w-full object-cover sm:aspect-[21/9]" />
              <div className="pf-scrim absolute inset-0 flex items-end p-6 sm:items-center sm:p-12">
                <p className="max-w-[30rem] text-[clamp(26px,3.4vw,46px)] font-extrabold leading-[1.05] tracking-[-0.035em] text-white">
                  Owners judge your brand long after the sale. <span className="text-bm-lime">Be there for setup, repairs and parts.</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Section id="surfaces" alt>
        <div className="max-w-3xl" data-reveal>
          <TwoTone lead="Each person who scans gets their own page." />
          <p className="mt-5 max-w-xl text-[16.5px] text-bm-muted">
            Your brand, your domain. We appear only as a small &ldquo;Powered by BrandedMark&rdquo; line.
          </p>
        </div>
        <div className="mt-12">
          <Surfaces />
        </div>
      </Section>

      <Section id="back-office">
        <div className="grid gap-6 lg:grid-cols-[1.4fr_1fr] lg:items-end">
          <div data-reveal>
            <TwoTone lead={backOffice.title[0]} rest={backOffice.title[1]} />
          </div>
          <p className="t-lede max-w-xl lg:justify-self-end" data-reveal>
            {backOffice.lede}
          </p>
        </div>
        <div className="pf-mock mt-12" data-reveal>
          <BackOffice unit={heroUnits[0]} />
        </div>
        <ul className="pf-bo-list">
          {backOffice.items.map((x, i) => (
            <li key={x.t} data-reveal style={{ ['--reveal-delay' as string]: `${(i % 3) * 0.06}s` }}>
              <div className="flex items-center gap-2.5">
                <h3>{x.t}</h3>
                {x.status !== 'live' && <StatusChip status={x.status} />}
              </div>
              <p>{x.d}</p>
            </li>
          ))}
        </ul>
      </Section>

      <Section id="standards" alt>
        <div className="mb-12 grid gap-6 lg:grid-cols-[1fr_1fr] lg:items-end">
          <div data-reveal>
            <TwoTone lead="Codes that last as long as the product." />
          </div>
          <div className="lg:justify-self-end" data-reveal>
            <SmartLink href="/why-trust-us" className="pk-textlink">
              What we put in the contract <Arrow />
            </SmartLink>
          </div>
        </div>
        <Standards />
      </Section>

      <Section alt>
        <Faq faqs={PLATFORM_FAQS} />
      </Section>

      <CtaBand source="platform" />
    </div>
  );
}

function PageStyles() {
  return (
    <style>{`
.v2 .pf-map { position: relative; }
.v2 .pf-bo-list { list-style: none; margin: 40px 0 0; padding: 0; display: grid; gap: 28px 40px; }
@media (min-width: 640px) { .v2 .pf-bo-list { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
@media (min-width: 1024px) { .v2 .pf-bo-list { grid-template-columns: repeat(3, minmax(0, 1fr)); } }
.v2 .pf-bo-list li { border-top: 2px solid #E4E3F0; padding-top: 16px; }
.v2 .pf-bo-list h3 { margin: 0; font-size: 17px; font-weight: 700; letter-spacing: -.015em; color: #241E61; }
.v2 .pf-bo-list p { margin: 6px 0 0; font-size: 15px; line-height: 1.55; color: #67648F; }
.v2 .pf-belief { padding: 8px 0 clamp(72px, 9vw, 128px); }
.v2 .pf-scrim { background: linear-gradient(0deg, rgba(24,19,67,.82) 0%, rgba(24,19,67,.25) 55%, rgba(24,19,67,0) 80%); }
@media (min-width: 640px) { .v2 .pf-scrim { background: linear-gradient(90deg, rgba(24,19,67,.85) 0%, rgba(24,19,67,.55) 38%, rgba(24,19,67,0) 65%); } }
.v2 .pf-spine-head { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 20px; padding-bottom: 28px; }
.v2 .pf-record { display: inline-block; padding: 14px 20px; border-radius: 16px; background: var(--bm-indigo); }
.v2 .pf-stages { border-top: 1px solid var(--bm-line); }
.v2 .pf-stage { display: grid; gap: 20px; padding: 32px 0; border-bottom: 1px solid var(--bm-line); }
.v2 .pf-stage-head { position: relative; }
.v2 .pf-node { display: none; }
.v2 .pf-caps { display: grid; gap: 0; }
.v2 .pf-caps > li + li { border-top: 1px solid var(--bm-line); }
.v2 .pf-cap { display: block; padding: 16px 0; }
@media (min-width: 900px) {
  .v2 .pf-stages { position: relative; border-top: 0; }
  .v2 .pf-stages::before { content: ''; position: absolute; left: 7px; top: 0; bottom: 0; width: 1px; background: var(--bm-line); }
  .v2 .pf-stage { grid-template-columns: 300px 1fr; gap: 48px; padding: 36px 0 36px 40px; border-bottom: 0; }
  .v2 .pf-stage + .pf-stage { border-top: 1px solid var(--bm-line); }
  .v2 .pf-node { display: block; position: absolute; left: -40px; top: 2px; width: 15px; height: 15px; border-radius: 50%; background: #fff; box-shadow: inset 0 0 0 2px var(--bm-indigo); }
  .v2 .pf-caps { grid-template-columns: 1fr 1fr; column-gap: 40px; }
  .v2 .pf-caps > li + li { border-top: 0; }
  .v2 .pf-caps > li { border-bottom: 1px solid var(--bm-line); }
  .v2 .pf-caps > li:nth-last-child(-n+2):nth-child(odd), .v2 .pf-caps > li:last-child { border-bottom: 0; }
  .v2 .pf-cap { padding: 4px 0 20px; }
  .v2 .pf-caps > li:nth-child(n+3) .pf-cap { padding-top: 20px; }
}
.v2 .pf-surface { background: #fff; border-radius: 22px; overflow: hidden; box-shadow: 0 0 0 1px var(--bm-line); }
.v2 .pf-surface-stage { display: flex; justify-content: center; padding: 40px 16px 0; height: 420px; overflow: hidden; background: linear-gradient(180deg, #EFEFF8 0%, #F7F7FC 100%); border-bottom: 1px solid var(--bm-line); }
.v2 .pf-zoom { zoom: 0.92; }
@media (max-width: 639px) { .v2 .pf-surface-stage { height: 300px; padding-top: 28px; } .v2 .pf-zoom { zoom: 0.7; } }
`}</style>
  );
}
